<script>
  import { onDestroy } from "svelte";
  export let data = {};
  import { lang } from "../lib/stores";
  import { geoAzimuthalEquidistant } from "d3";

  let selectedLang;
  const unsubscribe = lang.subscribe((value) => (selectedLang = value));
  onDestroy(unsubscribe);

  const songs = [[], [], []];
  const keys = Object.keys(data.info.songs);
  keys.forEach((k) => {
    const song = data.info.songs[k];
    songs[song.album].push(song);
  });
  console.log(songs);
</script>

<section class="chapter-4 chapter-inner">
  <div class="subtitle">
    <h3>
      {#if selectedLang === "it"}
        Qualche informazione tecnica
      {:else}
        Some technical information
      {/if}
    </h3>
    <h2>
      {#if selectedLang === "it"}
        Le nostre fonti
      {:else}
        Our sources
      {/if}
    </h2>
  </div>
  <div class="p">
    <p>
      {#if selectedLang === "it"}
        La prima attività che abbiamo dovuto completare è stata recuperare tutti
        i testi. Per farlo abbiamo consultato diverse fonti online; la migliore
        a nostro avviso è <a href="https://rapgenius.com" target="_blank"
          >RapGenius</a
        >: i testi sono ben organizzati e ci sono sembrati i più fedeli ai brani
        originali.
      {:else}
        <p>
          The first task we had to complete was to gather all the lyrics. To do
          this, we consulted several online sources; in our opinion, the best
          one is <a target="_blank" href="https://rapgenius.com">RapGenius</a>:
          the lyrics are well-organized, and they seemed to be the most accurate
          compared to the original tracks.
        </p>
      {/if}
    </p>
  </div>
  <div class="subtitle">
    <h3>
      {#if selectedLang === "it"}
        Qualche informazione tecnica
      {:else}
        Some technical information
      {/if}
    </h3>
    <h2>
      {#if selectedLang === "it"}
        Come abbiamo analizzato i testi
      {:else}
        How we analyzed the lyrics
      {/if}
    </h2>
  </div>
  <div class="p">
    <p>
      {#if selectedLang === "it"}
        Una volta recuperati i testi, ci siamo occupati di una prima "pulizia"
        manuale. In molti casi la stessa parola, e questa cosa è vera
        soprattutto per le esclamazioni, sono scritte in maniera differente nei
        diversi testi. Un esempio? <em>Hey</em> è talvolta scritta con la
        <em>y</em>, talvolta con la <em>i</em>. Abbiamo trovato diversi casi
        simili, prontamente corretti. Ma l'opera di revisione manuale non si è
        limitata a questo. Il testo di
        <em>Jennifer</em> (DNA, 2020), ad esempio, riporta il ritornello come:
      {:else}
        Once we gathered the lyrics, we carried out an initial manual
        "clean-up." In many cases, the same word—especially true for
        exclamations—was written differently in different versions of the
        lyrics. An example? <em>Hey</em> is sometimes written with a <em>y</em>,
        sometimes with an <em>i</em>. We found several similar cases, which we
        promptly corrected. However, the manual revision work was not limited to
        this. For example, the lyrics of <em>Jennifer</em>
        (DNA, 2020) show the chorus as follows:
      {/if}
    </p>
    <p>
      Oh, nari, nari, nari, ne (Wa3lach?)<br />
      <em>Jenni-Jenni-Jennifer</em> (I love you)
    </p>
    <p>
      {#if selectedLang === "it"}
        che noi abbiamo modificato in:
      {:else}
        which we chenged to:
      {/if}
    </p>
    <p>
      Oh, nari, nari, nari, ne (Wa3lach?)<br />
      <em>Jennifer</em> (I love you)
    </p>
    <p>
      {#if selectedLang === "it"}
        In sostanza: benché nel brano quel verso venga effettivamente
        pronunciato in quel modo, <em>Jenni-Jenni-Jennifer</em>
        non è una parola.
      {:else}
        In essence: although that line is indeed pronounced in that way in the
        song,
        <em>Jenni-Jenni-Jennifer</em> is not a word.
      {/if}
    </p>
    <p>
      {#if selectedLang === "it"}
        Una volta rivisti tutti i testi siamo passati alla loro <em
          >tokenizzazione</em
        >
        usando una libreria
        <a href="https://www.python.org/" target="_blank">Python</a>
        chiamata <a href="https://spacy.io/" target="_blank">spaCy</a>: si
        tratta di una libreria in grado di analizzare testi in diverse lingue, e
        trasformare ogni parola, appunto, in un token. Per l'italiano è in grado
        di riconoscere i verbi, trasformandoli all'infinito (così
        <em>ho mangiato</em>, ad esempio, viene riconosciuto come
        <em>mangiare</em>), riconoscere nomi e aggettivi trasformando ciascuno
        di questi nel corrispettivo maschile singolare (<em>belle</em>,
        <em>belli</em>, <em>bella</em>, a seguito della tokenizzazione,
        diventano tutti <em>bello</em>), scomporre le preposizioni articolate
        nei loro elementi costitutivi (<em>del</em> diventa <em>da il</em>). Il
        risultato della tokenizzazione è, per ogni brano, un lungo elenco di
        parole "semplficate". Per come è stato progettato, spaCy non applica
        alcuna trasformazione alle parole che non riconosce (in sostanza, le
        parole straniere), ma cerca di interpretare le parole che riconosce come
        italiane ed applicare ad esse le regole grammaticali "imparate" dal
        dataset di training: questo ha creato alcune imprecisioni che,
        nuovamente, abbiamo corretto a mano: la parola <em>minchia</em>, ad
        esempio, in alcuni contesti è stata tokenizzata come <em>minchiare</em>.
        Questo è dovuto al fatto che i registri dei testi di training sono
        completamente differenti da quelli dei testi di Ghali che fanno
        <em>un grande uso di slang</em>.
      {:else}
        Once all the lyrics were reviewed, we proceeded with their <em
          >tokenization</em
        >
        using a <a href="https://www.python.org/" target="_blank">Python</a>
        library called <a href="https://spacy.io/" target="_blank">spaCy</a>:
        this is a library capable of analyzing texts in various languages and
        transforming each word into a token. For Italian, it can recognize verbs
        and convert them to their infinitive forms (for example,
        <em>ho mangiato</em> -I ate- is recognized as <em>mangiare</em> -to
        eat), identify nouns and adjectives, transforming each of them into the
        corresponding masculine singular form (for instance, <em>belle</em>,
        <em>belli</em>, <em>bella</em> all become <em>bello</em> after
        tokenization), and break down articulated prepositions into their
        constituent parts (for example, <em>del</em>
        becomes <em>da il</em>).<br />
        The result of tokenization is, for each track, a long list of "simplified"
        words. As designed, spaCy does not apply any transformations to words it
        does not recognize (essentially, foreign words), but it attempts to interpret
        the words it recognizes as Italian and applies the grammatical rules "learned"
        from its training dataset. This created some inaccuracies, which we corrected
        manually: for instance, the word
        <em>minchia</em> was tokenized as <em>minchiare</em> in some contexts. This
        is because the registers in the training texts are completely different from
        those in Ghali's lyrics, which make extensive use of slang.
      {/if}
    </p>
    <p>
      {#if selectedLang === "it"}
        Per la fase successiva, abbiamo scritto dei piccoli programmi usando <a
          href="https://nodejs.org/en"
          target="_blank">NodeJS</a
        >
        che si sono occupati di <em>contare i token</em> generati nel passaggio precedente,
        e riaggregarli attorno ai temi che abbiamo scelto. Anche la scelta dei temi
        è stato un processo manuale: abbiamo letto e riletto tutti i testi, e creato
        degli elenchi di parole che rappresentassero ciascun tema. Il risultato di
        tutti questi passaggi è il database che abbiamo utilizzato per generare i
        grafici e le statistiche che hai appena letto.
      {:else}
        For the next phase, we wrote small programs using <a
          href="https://nodejs.org/en"
          target="_blank">NodeJS</a
        > that handled counting the tokens generated in the previous step and reaggregating
        them around the themes we selected. The selection of themes was also a manual
        process we read and re-read all the lyrics and created lists of words that
        represented each theme. The result of all these steps is the database we
        used to generate the graphs and statistics you have just read.
      {/if}
    </p>
    <p>
      {#if selectedLang === "it"}
        Le fonti riguardanti il lessico italiano sono riportate direttamente
        dove vengono citate, ma in generale ci siamo riferiti a <a
          href="https://www.treccani.it/magazine/lingua_italiana/domande_e_risposte/lessico/lessico_267.html"
          target="_blank">questo articolo sul magazine Treccani</a
        >, e a questi due rispettivamente pubblicati su
        <a
          href="https://www.ilgiornale.it/news/l-italiano-medio-usa-solo-800-parole.html"
          target="_blank">Il Giornale</a
        >
        e
        <a
          href="https://www.coffeewriting.it/coffee-corretto/parole-parole-parole-ma-quante-ne-utilizziamo-davvero/#:~:text=Quotidianamente%20un%20italiano%20medio%20fa,composto%20da%20sole%202.000%20parole."
          target="_blank">CoffeeWriting</a
        >, il sito di una copywriter appassionata di bella scrittura.
      {:else}
        The sources regarding the Italian lexicon are provided directly where
        they are cited, but in general, we referred to this article from the <a
          href="https://www.treccani.it/magazine/lingua_italiana/domande_e_risposte/lessico/lessico_267.html"
          target="_blank">Treccani magazine</a
        >, and to these two articles published in
        <a
          href="https://www.ilgiornale.it/news/l-italiano-medio-usa-solo-800-parole.html"
          target="_blank">Il Giornale</a
        >
        and
        <a
          href="https://www.coffeewriting.it/coffee-corretto/parole-parole-parole-ma-quante-ne-utilizziamo-davvero/#:~:text=Quotidianamente%20un%20italiano%20medio%20fa,composto%20da%20sole%202.000%20parole."
          target="_blank">CoffeeWriting</a
        >.
      {/if}
    </p>
  </div>
  <div class="subtitle">
    <h3>
      {#if selectedLang === "it"}
        Qualche informazione tecnica
      {:else}
        Some technical information
      {/if}
    </h3>
    <h2>
      {#if selectedLang === "it"}
        Come abbiamo costruito questa pagina
      {:else}
        How we built this page
      {/if}
    </h2>
  </div>
  <div class="p">
    <p>
      {#if selectedLang === "it"}
        La pagina che stai leggendo è stata costruita con in <em>HTML5</em> e
        <em>CSS3</em>
        usando <a href="https://svelte.dev/" target="_blank">SvelteJs</a>. Tutti
        i grafici sono renderizzati utilizzando
        <a href="https://d3js.org/" target="_blank">D3Js</a>.
      {:else}
        The page you are reading was built with <em>HTML5</em> and <em>CSS3</em>
        using
        <a href="https://svelte.dev/" target="_blank">SvelteJs</a>. All the
        graphs are rendered using
        <a href="https://d3js.org/" target="_blank">D3Js</a>.
      {/if}
    </p>
  </div>
  <div class="subtitle">
    <h3>
      {#if selectedLang === "it"}
        Per leggere i testi
      {:else}
        To read the lyrics
      {/if}
    </h3>
    <h2>
      {#if selectedLang === "it"}
        Tutte le parole di Ghali
      {:else}
        All the words of Ghali
      {/if}
    </h2>
  </div>
  <div class="songs-wrapper">
    {#each data.info.albums as album, index}
      <div class="album">
        <header>
          <img src={album.cover} alt={album.title} />
          <h2>{album.title} ({album.year})</h2>
          <div class="shadow" />
        </header>
        <div class="songs">
          {#each songs[index] as song}
            <div class="song">
              <a href={song.source} target="_blank">{song.title}</a>
            </div>
          {/each}
        </div>
      </div>
    {/each}
  </div>
  <div class="subtitle">
    <h3>
      {#if selectedLang === "it"}
        Per saperne di più
      {:else}
        To learn more
      {/if}
    </h3>
    <h2>
      {#if selectedLang === "it"}
        Ghali, Baggio, l'hip hop
      {:else}
        Ghali, Baggio, the hip hop
      {/if}
    </h2>
  </div>
  <div class="p">
    <p>
      {#if selectedLang === "it"}
        Questa pagina ha avuto una gestazione complessa, non a caso si occupa
        solo dei primi tre album di Ghali, quando da poco ne è uscito il quinto.
        Però volevamo arrivare in fondo, e pubblicarla, essenzialmente per un
        paio di motivi: il primo è che <em>siamo anche noi di Baggio</em> (e ci
        immedesimiamo molto in questo pezzo di
        <a href="https://www.youtube.com/watch?v=mq7xG5bxP48" target="_blank"
          >Davide Calgaro</a
        >). Il secondo è che
        <em>ricordiamo Ghali dai tempi dei Troupe d'Elite</em>, e siamo molto
        contenti della sua crescita come musicista ma soprattutto come uomo. La
        terza è che per noi è un po' un ritorno alle origini: dopo aver lavorato
        per tanti anni a
        <a href="https://hotmc.com" target="_blank">Hotmc.com</a>
        l'hip hop ci mancava. Ci auguriamo che quello che avete appena letto vi sia
        piaciuto.
      {:else}
        This page had a complex development process, and it's no coincidence
        that it only covers Ghali's first three albums, even though his fifth
        has just been released. However, we wanted to finish it and publish it,
        mainly for a couple of reasons: the first is that <em
          >we are also from Baggio</em
        >
        (and we really relate to this piece by
        <a href="https://www.youtube.com/watch?v=mq7xG5bxP48" target="_blank"
          >Davide Calgaro</a
        >). The second is that we remember Ghali from his Troupe d'Elite days,
        and we're very happy with his growth as a musician, but especially as a
        person. The third is that, for us, it's a bit of a return to our roots:
        after working for many years at
        <a href="https://hotmc.com" target="_blank">Hotmc.com</a>, we missed hip
        hop. We hope that what you've just read was enjoyable for you.
      {/if}
    </p>
    <p>
      {#if selectedLang === "it"}
        Per chi fosse interessato leggere delle analisi più serie sull'argomento
        ci sentiamo di consigliare i due articoli già citati di <em
          >Justine Noyer</em
        >,
        <a
          href="https://hal.science/hal-04347411/file/Justine_Noyer_Le_scelte_linguistiche_nel_rap_italiano_di_oggi_il_caso_di_Ghali.pdf"
          target="_blank"
          >Le scelte linguistiche nel rap italiano di oggi: il caso di Ghali</a
        >
        e <em>Jacopo Ferrari</em>,
        <a
          href="https://riviste.unimi.it/index.php/LCdM/article/download/10309/9680/30632"
          target="_blank"
          >La lingua dei rapper figli dell'immigrazione in Italia</a
        >.
      {:else}
        For those interested in reading more serious analyses on the subject, we
        would recommend the two previously cited articles:
        <a
          href="https://hal.science/hal-04347411/file/Justine_Noyer_Le_scelte_linguistiche_nel_rap_italiano_di_oggi_il_caso_di_Ghali.pdf"
          target="_blank"
          >Le scelte linguistiche nel rap italiano di oggi: il caso di Ghali</a
        >
        by <em>Justine Noyer</em> and
        <a
          href="https://riviste.unimi.it/index.php/LCdM/article/download/10309/9680/30632"
          target="_blank"
          >La lingua dei rapper figli dell'immigrazione in Italia</a
        >
        by <em>Jacopo Ferrari</em>.
      {/if}
    </p>
  </div>
  <div class="subtitle">
    <h3>
      {#if selectedLang === "it"}
        Hai notato qualche errore?
      {:else}
        Did you notice any mistakes?
      {/if}
    </h3>
    <h2>
      {#if selectedLang === "it"}
        Faccelo sapere!
      {:else}
        Let us know!
      {/if}
    </h2>
  </div>
  <div class="p">
    <p>
      {#if selectedLang === "it"}
        Come hai letto, gran parte del lavoro di aggregazione e pulizia dei
        testi è stato fatto manualmente. Noi ci abbiamo messo tutta la nostra
        attenzione e buona fede, ma qualche errore può capitare. Se hai notato
        qualcosa che non va, contattaci tramite i nostri social: siamo <em
          >@visualizenews</em
        >
        su <a href="https://x.com/visualizenews" target="_twitter">X</a> e su
        <a href="https://instagram.com/visualizenews" target="_twitter"
          >Instagram</a
        >.
      {:else}
        As you have read, much of the work of aggregating and cleaning the
        lyrics was done manually. We put all our attention and good faith into
        it, but some errors may have occurred. If you notice anything wrong,
        please contact us through our social media: we are <em
          >@visualizenews</em
        >
        on <a href="https://x.com/visualizenews" target="_twitter">X</a> and
        <a href="https://instagram.com/visualizenews" target="_twitter"
          >Instagram</a
        >.
      {/if}
    </p>
  </div>
</section>

<style>
  .chapter-4 .songs-wrapper {
    display: block;
    margin-bottom: 2rem;
  }

  .chapter-4 .album {
    margin-bottom: 2rem;
  }

  .chapter-4 .album img {
    aspect-ratio: 1 / 1;
    display: block;
    height: auto;
    width: 100%;
  }

  .chapter-4 header {
    aspect-ratio: 1 / 1;
    margin-bottom: 1rem;
    position: relative;
    width: 100%;
  }

  .chapter-4 .album h2 {
    bottom: 2rem;
    position: absolute;
    right: 1rem;
    text-align: right;
    z-index: 2;
  }

  .chapter-4 .shadow {
    background: #00000066;
    bottom: 0;
    height: auto;
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
    width: auto;
    z-index: 1;
  }

  @media screen and (min-width: 768px) {
    .chapter-4 .songs-wrapper {
      display: flex;
      flex-wrap: nowrap;
      margin: 0 -1rem 4rem -1rem;
    }
    .chapter-4 .album {
      flex: 0 0 33%;
      padding: 0 1rem;
    }
  }
</style>
